/**
 * TenantConfigContext
 *
 * OEM/ホワイトラベル対応のためのテナント設定 Context
 * - 認証前: tenant-config API（public）から brand/theme を取得
 * - 認証後: bootstrap API から全設定を取得
 *
 * Related: OEM-BASE-03
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { useAuth } from "./AuthContext";
import {
  BEAUTY_SNS_LAB_FALLBACK_CONFIG,
  BEAUTY_SNS_LAB_TENANT_KEY,
  isBeautySnsLabHost,
  isBeautySnsLabPath,
  isBeautySnsLabTenantKey,
  resolvePublicAssetUrl,
} from "@/config/beautySnsLab";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface TenantBrand {
  name: string;
  tagline?: string | null;
  logo_url?: string | null;
  favicon_url?: string | null;
  og_image_url?: string | null;
}

export interface TenantTheme {
  primary_color: string;
  secondary_color?: string | null;
  font_family?: string | null;
}

export interface TenantFeatureFlags {
  payment_enabled: boolean;
  line_enabled: boolean;
  vision_api_enabled: boolean;
  human_escalation_enabled: boolean;
  analytics_enabled: boolean;
  public_signup_enabled?: boolean;
  score_display_enabled?: boolean;
}

export interface TenantSupport {
  email?: string | null;
  company_name?: string | null;
  help_url?: string | null;
}

export interface TenantLegal {
  terms_url?: string | null;
  privacy_url?: string | null;
}

export interface TenantConfig {
  tenant_key: string;
  brand: TenantBrand;
  theme: TenantTheme;
  domain?: string | null;
  feature_flags?: TenantFeatureFlags;
  support?: TenantSupport;
  legal?: TenantLegal;
}

interface TenantConfigContextValue {
  config: TenantConfig | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Default values
// ─────────────────────────────────────────────────────────────────────────────

const DEFAULT_BRAND: TenantBrand = {
  name: "JARVIS LMS",
  tagline: "AI搭載学習プラットフォーム",
};

const DEFAULT_THEME: TenantTheme = {
  primary_color: "#3B82F6",
};

const DEFAULT_CONFIG: TenantConfig = {
  tenant_key: "default",
  brand: DEFAULT_BRAND,
  theme: DEFAULT_THEME,
};

// ─────────────────────────────────────────────────────────────────────────────
// Utilities
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Convert hex color to HSL string for shadcn/ui CSS variables
 * @param hex - Hex color string (e.g., "#3B82F6")
 * @returns HSL string (e.g., "217 91% 60%") or null if invalid
 */
function hexToHsl(hex: string): string | null {
  // Remove # if present
  const cleanHex = hex.replace(/^#/, "");

  // Parse hex to RGB
  const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(cleanHex);
  if (!result) return null;

  const r = parseInt(result[1], 16) / 255;
  const g = parseInt(result[2], 16) / 255;
  const b = parseInt(result[3], 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  // Return HSL without commas (shadcn format)
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/**
 * Calculate relative luminance of a hex color
 * Used to determine if foreground should be light or dark
 */
function getRelativeLuminance(hex: string): number {
  const cleanHex = hex.replace(/^#/, "");
  const result = /^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(cleanHex);
  if (!result) return 0.5;

  const [r, g, b] = [result[1], result[2], result[3]].map((c) => {
    const val = parseInt(c, 16) / 255;
    return val <= 0.03928 ? val / 12.92 : Math.pow((val + 0.055) / 1.055, 2.4);
  });

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Get contrasting foreground HSL based on background color luminance
 * Returns light foreground for dark backgrounds and vice versa
 */
function getContrastingForegroundHsl(hexColor: string): string {
  const luminance = getRelativeLuminance(hexColor);
  // WCAG recommends 4.5:1 contrast ratio for normal text
  // Use light foreground (98% lightness) for dark backgrounds, dark (5%) for light
  return luminance > 0.4 ? "222 47% 11%" : "210 40% 98%";
}

function getCurrentHostFallbackConfig(): TenantConfig {
  const envTenantKey =
    import.meta.env.VITE_TENANT_KEY || import.meta.env.VITE_OEM_TENANT_KEY;

  if (
    isBeautySnsLabTenantKey(envTenantKey) ||
    (typeof window !== "undefined" &&
      (isBeautySnsLabHost(window.location?.hostname) ||
        isBeautySnsLabPath(window.location?.pathname)))
  ) {
    return BEAUTY_SNS_LAB_FALLBACK_CONFIG;
  }

  return DEFAULT_CONFIG;
}

function setMetaByName(name: string, content?: string | null) {
  if (!content) return;

  let meta = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = name;
    document.head.appendChild(meta);
  }
  meta.content = content;
}

function setMetaByProperty(property: string, content?: string | null) {
  if (!content) return;

  let meta = document.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("property", property);
    document.head.appendChild(meta);
  }
  meta.content = content;
}

function setLinkByRel(rel: string, href?: string | null, attributes: Record<string, string> = {}) {
  if (!href) return;

  let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement("link");
    link.rel = rel;
    document.head.appendChild(link);
  }

  link.href = href;
  Object.entries(attributes).forEach(([key, value]) => {
    link.setAttribute(key, value);
  });
}

function applyDocumentBranding(config: TenantConfig) {
  if (typeof document === "undefined") return;

  const brandName = config.brand?.name || DEFAULT_BRAND.name;
  const description = config.brand?.tagline || DEFAULT_BRAND.tagline || brandName;
  const title = `${brandName} - ${description}`;
  const origin =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : undefined;
  const ogImage = resolvePublicAssetUrl(config.brand?.og_image_url, origin);
  const favicon = resolvePublicAssetUrl(config.brand?.favicon_url, origin);
  const canonical =
    config.domain && config.domain.trim()
      ? `https://${config.domain.replace(/^https?:\/\//, "").replace(/\/+$/, "")}/`
      : origin || undefined;

  document.title = title;
  setMetaByName("description", description);
  setMetaByName("author", brandName);
  setMetaByName("theme-color", config.theme?.primary_color);
  setMetaByName("twitter:card", ogImage ? "summary_large_image" : "summary");
  setMetaByName("twitter:title", brandName);
  setMetaByName("twitter:description", description);
  setMetaByName("twitter:image", ogImage);

  setMetaByProperty("og:title", brandName);
  setMetaByProperty("og:description", description);
  setMetaByProperty("og:type", "website");
  setMetaByProperty("og:image", ogImage);

  setLinkByRel("canonical", canonical);
  setLinkByRel("icon", favicon, { type: "image/png" });
  setLinkByRel("apple-touch-icon", favicon);
}

// ─────────────────────────────────────────────────────────────────────────────
// Context
// ─────────────────────────────────────────────────────────────────────────────

const TenantConfigContext = createContext<TenantConfigContextValue>({
  config: null,
  loading: true,
  error: null,
  refetch: async () => {},
});

// ─────────────────────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────────────────────

interface TenantConfigProviderProps {
  children: ReactNode;
}

export function TenantConfigProvider({ children }: TenantConfigProviderProps) {
  const { user, loading: authLoading } = useAuth();
  const [config, setConfig] = useState<TenantConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const apiBaseUrl =
    import.meta.env.VITE_API_BASE_URL ||
    (import.meta.env.DEV ? "http://localhost:8000" : "");

  // Fetch public tenant config (no auth required)
  const fetchPublicConfig = useCallback(async () => {
    try {
      const host = window.location.hostname;
      const hostFallbackConfig = getCurrentHostFallbackConfig();

      if (
        hostFallbackConfig.tenant_key === BEAUTY_SNS_LAB_TENANT_KEY &&
        !isBeautySnsLabHost(host)
      ) {
        setConfig(hostFallbackConfig);
        return;
      }

      const response = await fetch(
        `${apiBaseUrl}/api/v1/public/tenant-config?host=${encodeURIComponent(host)}`
      );

      if (!response.ok) {
        // Fallback to current-host defaults if tenant not found
        if (response.status === 404) {
          setConfig(getCurrentHostFallbackConfig());
          return;
        }
        throw new Error(`Failed to fetch tenant config: ${response.status}`);
      }

      const data = await response.json();
      setConfig(data);
    } catch (err) {
      console.warn("Failed to fetch public tenant config, using defaults:", err);
      setConfig(getCurrentHostFallbackConfig());
    }
  }, [apiBaseUrl]);

  // Fetch full bootstrap config (auth required)
  const fetchBootstrapConfig = useCallback(async () => {
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;

      if (!token) {
        // No token, fall back to public config
        await fetchPublicConfig();
        return;
      }

      const response = await fetch(`${apiBaseUrl}/api/v1/bootstrap`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch bootstrap config: ${response.status}`);
      }

      const data = await response.json();
      setConfig(data);
    } catch (err) {
      console.warn("Failed to fetch bootstrap config, falling back to public:", err);
      await fetchPublicConfig();
    }
  }, [apiBaseUrl, fetchPublicConfig]);

  // Main fetch logic
  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      if (user) {
        // Authenticated: use bootstrap API
        await fetchBootstrapConfig();
      } else {
        // Not authenticated: use public API
        await fetchPublicConfig();
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      setError(message);
      setConfig(getCurrentHostFallbackConfig());
    } finally {
      setLoading(false);
    }
  }, [user, fetchBootstrapConfig, fetchPublicConfig]);

  // Refetch function for manual refresh
  const refetch = useCallback(async () => {
    await fetchConfig();
  }, [fetchConfig]);

  // Fetch config when auth state changes
  useEffect(() => {
    if (!authLoading) {
      fetchConfig();
    }
  }, [authLoading, user, fetchConfig]);

  useEffect(() => {
    if (!config) return;
    applyDocumentBranding(config);
  }, [config]);

  // OEM-VISUAL-01: Apply theme colors to CSS variables
  useEffect(() => {
    if (!config?.theme) return;

    const root = document.documentElement;
    const { primary_color, secondary_color } = config.theme;

    // Apply primary color (shadcn/ui uses HSL format)
    if (primary_color) {
      const primaryHsl = hexToHsl(primary_color);
      if (primaryHsl) {
        root.style.setProperty("--primary", primaryHsl);
        // Calculate and set contrasting foreground for accessibility
        root.style.setProperty("--primary-foreground", getContrastingForegroundHsl(primary_color));
      }
      root.style.setProperty("--oem-primary-hex", primary_color);
    }

    // Apply secondary color
    if (secondary_color) {
      const secondaryHsl = hexToHsl(secondary_color);
      if (secondaryHsl) {
        root.style.setProperty("--secondary", secondaryHsl);
        root.style.setProperty("--secondary-foreground", getContrastingForegroundHsl(secondary_color));
        // Accent follows secondary for consistency in shadcn/ui
        root.style.setProperty("--accent", secondaryHsl);
        root.style.setProperty("--accent-foreground", getContrastingForegroundHsl(secondary_color));
      }
      root.style.setProperty("--oem-secondary-hex", secondary_color);
    }

    // Log for debugging in non-production
    if (import.meta.env.DEV) {
      console.log("[TenantConfig] Applied theme:", {
        primary_color,
        secondary_color,
        primary_hsl: hexToHsl(primary_color),
        primary_foreground: primary_color ? getContrastingForegroundHsl(primary_color) : null,
      });
    }
  }, [config?.theme]);

  return (
    <TenantConfigContext.Provider value={{ config, loading, error, refetch }}>
      {children}
    </TenantConfigContext.Provider>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useTenantConfig(): TenantConfigContextValue {
  const context = useContext(TenantConfigContext);
  if (!context) {
    throw new Error("useTenantConfig must be used within a TenantConfigProvider");
  }
  return context;
}

// Convenience hooks for specific parts of the config
export function useBrand(): TenantBrand {
  const { config } = useTenantConfig();
  return config?.brand ?? DEFAULT_BRAND;
}

export function useTheme(): TenantTheme {
  const { config } = useTenantConfig();
  return config?.theme ?? DEFAULT_THEME;
}

export function useFeatureFlags(): TenantFeatureFlags | undefined {
  const { config } = useTenantConfig();
  return config?.feature_flags;
}

export function useSupport(): TenantSupport | undefined {
  const { config } = useTenantConfig();
  return config?.support;
}

export function useLegal(): TenantLegal | undefined {
  const { config } = useTenantConfig();
  return config?.legal;
}
